<template>
  <div>
    <div v-if="country && country.usdExchangeRate">
      <SectionTitle text="Валютный курс" class="mb-3">
        <template #aside>
          <div class="analytics-source-text">Источник: Банк международных расчетов</div>
        </template>
      </SectionTitle>

      <div class="exchangeRate d-flex align-items-center justify-content-between">
        <span class="exchangeRate-text">1 USD</span>
        <IconComponent class="exchangeRate-text" name="2arrow" />
        <span class="exchangeRate-text">{{ country.usdExchangeRate + ' ' + (country.currency ?? '') }}</span>
        <span v-if="country.usdExchangeRateDate" class="analytics-source-text" style="margin-bottom: 13px">
          на {{ $momentFormat(country.usdExchangeRateDate, 'DD.MM.YYYY') }}
        </span>
      </div>
    </div>
    <SectionTitle text="Ключевые показатели" class="mb-3 mt-4" />
    <ul class="figures-key__list overflow-auto">
      <li
        @click="currentInflationMoreInfo ? selectIndicators(1, 'Инфляция, %') : null"
        :class="[{ active: activeTab === 1 }, { 'more-info-hidden': !currentInflationMoreInfo }]"
        class="figures-key__item"
      >
        <span class="figures-key__title">Инфляция</span>
        <!-- <span v-if="currentInflation" class="figures-key__title">{{ currentInflation.year }} г.</span> -->
        <span class="figures-key__title">
          Источник:
          <br />
          Trading Economics
          <br />
          По данным за <span v-if="currentInflation">{{ $momentFormat(currentInflation.period, 'MM.YYYY') }}</span
          ><span v-else>&mdash;</span>
        </span>
        <div class="figures-key__card d-flex justify-content-between align-items-center">
          <template v-if="currentInflation && currentInflation.previous">
            <i
              v-if="currentInflation.previous >= currentInflation.rawValue"
              class="fas fa-solid mr-1 text-red fa-arrow-down"
            ></i>
            <i
              v-if="currentInflation.previous < currentInflation.rawValue"
              class="fas fa-solid mr-1 text-green fa-arrow-up"
            ></i>
          </template>
          <template v-else>
            <i class="fa"></i>
          </template>
          <span v-if="currentInflation">{{ currentInflation.value }}</span>
          <span v-else>&mdash;</span>
          <p>%</p>
        </div>
        <button class="btn-default" v-if="currentInflationMoreInfo">
          <IconComponent name="chartIcon" />Больше информации
        </button>
      </li>
      <li
        @click="rateMoreInfo ? selectIndicators(2, 'Учетная ставка ЦБ, %') : null"
        class="figures-key__item"
        :class="[{ active: activeTab === 2 }, { 'more-info-hidden': !rateMoreInfo }]"
      >
        <span class="figures-key__title figures-key__title-key">Учетная ставка ЦБ</span>
        <span class="figures-key__title">
          Источник:
          <br />
          Trading Economics
          <br />
          По данным на <span v-if="currentBankRate">{{ currentBankRate.year }}</span
          ><span v-else>&mdash;</span> год
        </span>
        <div class="figures-key__card d-flex justify-content-between align-items-center">
          <template v-if="currentBankRate && currentBankRate.previous">
            <i
              v-if="currentBankRate.previous < currentBankRate.rawValue"
              class="fas fa-solid mr-1 text-red fa-arrow-down"
            ></i>
            <i
              v-if="currentBankRate.previous > currentBankRate.rawValue"
              class="fas fa-solid mr-1 text-green fa-arrow-up"
            ></i>
          </template>
          <template v-else>
            <i class="fa"></i>
          </template>
          <span v-if="currentBankRate">{{ currentBankRate.value }}</span>
          <span v-else>&mdash;</span>
          <p>%</p>
        </div>
        <button class="btn-default" v-if="rateMoreInfo"><IconComponent name="chartIcon" />Больше информации</button>
      </li>
    </ul>
  </div>

  <div class="graph">
    <SectionTitle :text="titleTab" />
    <template v-if="activeTab === 1">
      <EChartsVerticalBar
        :key="chartUpdater"
        formatLabel=" %"
        :labels="inflationChartLabels"
        :dataset="inflationChartDataset"
        :height="400"
        :legendSelectable="false"
        :formatValueFunction="formatInflationValue"
        :showSeriesLabel="false"
      />
    </template>

    <template v-if="activeTab === 2">
      <EChartsVerticalBar
        :key="chartUpdater"
        formatLabel=" %"
        :labels="bankRatesChartLabels"
        :dataset="bankRatesChartDataset"
        :height="400"
        :legendSelectable="false"
        :formatValueFunction="formatBankRateValue"
        :showSeriesLabel="false"
      />
    </template>
    <MonetaryChart v-if="!activeTab" :countryId="countryId" />
  </div>
</template>

<script>
  import SectionTitle from '@/common/components/redesigned/SectionTitle/SectionTitle.vue';
  import IconComponent from '@/common/ui/IconComponent.vue';

  import EChartsVerticalBar from '@/components/ChartComponents/apacheEcharts/EChartsVerticalBar.vue';

  //import constants from '../../../common/constants';
  import IndicatorApi from '@/modules/admin/api/indicatorVisibility';
  import CountryApi from '@/modules/nsi/api/country';

  import Api from '../api';
  import MonetaryChart from './MonetaryChart.vue';

  export default {
    name: 'MonetaryPolicy',
    components: { EChartsVerticalBar, IconComponent, MonetaryChart, SectionTitle },
    props: {
      countryId: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        activeTab: null,
        titleTab: '',
        inflation: [],
        chartUpdater: 1,
        country: null,
        currentInflationMoreInfo: true,
        rateMoreInfo: true,
        bankRates: [],
        currentBankRate: null,
      };
    },
    created() {
      if (this.countryId) {
        this.loadIndicatorVisibility();
        this.loadData();
      }
    },
    methods: {
      loadIndicatorVisibility() {
        IndicatorApi.search().then((response) => {
          this.currentInflationMoreInfo = response.data[8].moreInfoVisible;
          this.rateMoreInfo = response.data[9].moreInfoVisible;
        });
      },
      loadData() {
        const request = {
          countryId: this.countryId,
        };
        Api.searchInflation(request).then((response) => {
          this.inflation = response.data.items.map((x) => ({
            period: x.period,
            value: x.value,
            color: '#96d2e7',
          }));
          if (this.inflation.length) this.inflation[0].color = '#0093BA';
          this.chartUpdater++;
        });
        Api.searchBankRates(this.countryId).then((response) => {
          this.bankRates = response.data.map((x) => ({
            year: x.text,
            value: x.id,
          }));
          this.chartUpdater++;

          this.currentBankRate = null;
          if (this.bankRates.length) {
            let v = this.bankRates[this.bankRates.length - 1];
            this.currentBankRate = {
              year: v.year,
              rawValue: v.value,
              value: v.value.toFixed(2).replace('.', ','),
            };

            //ищем предыдущий год
            if (this.bankRates.length > 1) {
              let prev = this.bankRates.slice(-2);
              if (prev != null) {
                this.currentBankRate.previous = prev.value;
              }
            }
          }
        });
        CountryApi.find(this.countryId)
          .then((response) => {
            this.country = response.data;
          })
          .catch(() => {
            this.country = null;
          });
      },
      selectIndicators(id, text) {
        const isCurrent = this.activeTab === id;

        this.activeTab = isCurrent ? null : id;
        this.titleTab = isCurrent ? '' : text;
      },
      formatInflationValue(value) {
        return Number(value).toFixed(2).replace('.', ',');
      },
      formatBankRateValue(value) {
        return value != null && value !== undefined ? String(value).replace('.', ',') : '';
      },
    },
    watch: {
      countryId: function () {
        this.loadData();
      },
    },
    computed: {
      currentInflation() {
        if (!this.inflation.length) return 0;

        let v = this.inflation[this.inflation.length - 1];
        let result = {
          period: v.period,
          rawValue: v.value,
          value: v.value.toFixed(2).replace('.', ','),
          previous: null,
        };

        //ищем предыдущий
        if (this.inflation.length >= 2) {
          let prev = this.inflation[this.inflation.length - 2];
          if (prev != null) {
            result.previous = prev.value;
          }
        }
        return result;
      },
      inflationChartLabels() {
        return this.inflation.map((x) => this.$momentFormat(x.period, 'MM.YYYY'));
      },
      inflationChartDataset() {
        return [
          {
            label: '',
            data: this.inflation.map((x) => Number(x.value.toFixed(2))),
            colors: this.inflation.map((x) => x.color),
          },
        ];
      },
      bankRatesChartLabels() {
        return this.bankRates.map((x) => x.year);
      },
      bankRatesChartDataset() {
        return [
          {
            label: '',
            data: this.bankRates.map((x) => x.value),
            color: '#01A39D',
          },
        ];
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '../index';
  .select2::v-deep {
    outline: none;
    // Контейнер ширина
    .select2-container {
      outline: none;
      width: 100% !important;
      max-width: 100% !important;
      border-radius: 40px;
      // Текст
      .select2-selection__rendered {
        outline: none;
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        color: $green;
      }
    }
    // Контейнер высота
    .select2-container--default .select2-selection--single {
      outline: none;
      height: auto !important;
      border: 1px solid transparent !important;
      background-color: transparent !important;
    }
    .select2-container--default .select2-selection--single .select2-selection__arrow {
      outline: none;
      top: 50% !important;
      width: 10px;
      right: 14px;
      transform: translateY(-50%) !important;
    }
    // Плейсхолдер
    .select2-container--default .select2-selection--single .select2-selection__placeholder {
      outline: none;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.25px;
      color: #989898;
    }
    /* Модификация стрелки */
    .select2-container--default .select2-selection--single .select2-selection__arrow b {
      outline: none;
      border-color: unset;
      border-style: unset;
      border-width: unset;
      width: 10px;
      height: 10px;
      border-radius: 1px;
      border-left: 3px solid $green !important;
      border-bottom: 3px solid $green !important;
      transform: rotate(315deg);
      transition: 0.3s;
      mask-image: unset;
      margin-left: -6px;
      margin-top: -11.5px !important;
    }
    .select2-container--default.select2-container--open .select2-selection--single .select2-selection__arrow b {
      margin-top: -7.5px !important;
      transform: rotate(135deg);
    }
  }

  .figures-key__title-key {
    white-space: nowrap;
    max-width: 100px;
  }
  .more-info-hidden {
    padding-bottom: 26px;
    pointer-events: none;
  }
</style>
